import React, { useState, useEffect } from 'react';
import { 
  getPermissionTemplates, 
  getPermissionTemplateByRole, 
  updatePermissionTemplate,
  initializeDefaultPermissions,
  PermissionTemplate 
} from '../../services/permissionService';

interface Role {
  name: string;
  description: string;
  value: string;
}

interface Section {
  name: string;
  id: string;
}

const AccessControl: React.FC = () => {
  // Roles match the backend model roles
  const roles: Role[] = [
    { name: 'سوپرادمین', description: 'دسترسی کامل به تمام بخش های سیستم', value: 'SuperAdmin' },
    { name: 'پذیرشگر', description: 'دسترسی به مدیریت درخواست ها و پردازش اطلاعات پایه', value: 'Operator' },
    { name: 'حسابدار', description: 'دسترسی به بخش های مالی و پرداخت', value: 'Cashier' },
    { name: 'IT', description: 'دسترسی به بخش های مدیریتی و گزارشات', value: 'Supervisor' },
    { name: 'جوابدهی', description: 'دسترسی به بخش های آرشیو و سوابق', value: 'Archive' }
  ];

  // Sections match the backend model sections
  const sections: Section[] = [
    { name: 'مدیریت کاربران', id: 'users' },
    { name: 'مدیریت تیکت‌ها', id: 'tickets' },
    { name: 'دستورالعمل‌ها', id: 'instructions' },
    { name: 'درخواست های تصویر جواب آزمایش', id: 'image_reports' },
    { name: 'سایر درخواست ها', id: 'other_requests' },
    { name: 'نهایی و پرداخت شده', id: 'completed_requests' },
    { name: 'درحال اجرا', id: 'ongoing_requests' },
    { name: 'قوانین', id: 'rules' },
    { name: 'اطلاعیه‌ها', id: 'announcements' },
    { name: 'پیام‌ها', id: 'message' },
    { name: 'پیامک‌ها', id: 'sms' },
    { name: 'تقویم', id: 'calendar' },
    { name: 'بیمه', id: 'insurance' },
    { name: 'نتایج آزمایش', id: 'test_result' },
    { name: 'پرداخت‌های باقیمانده', id: 'remaining_payment' },
    { name: 'گزارشات', id: 'reports' },
    { name: 'مناطق', id: 'areas' },
    { name: 'لینک‌ها', id: 'links' },
    { name: 'راهنما', id: 'guides' },
    { name: 'سوالات متداول', id: 'faqs' },
    { name: 'پکیج‌ها', id: 'packages' },
    { name: 'تنظیمات عمومی', id: 'general_settings' }
  ];

  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [permissionTemplates, setPermissionTemplates] = useState<PermissionTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<PermissionTemplate | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch permission templates on component mount
  useEffect(() => {
    fetchPermissions();
  }, []);

  // Set initial selectedTemplate when permissionTemplates or selectedRole changes
  useEffect(() => {
    if (selectedRole && permissionTemplates.length > 0) {
      const template = permissionTemplates.find(t => t.role === selectedRole);
      setSelectedTemplate(template || null);
    } else {
      setSelectedTemplate(null);
    }
  }, [selectedRole, permissionTemplates]);

  // Fetch all permission templates from the server
  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const templates = await getPermissionTemplates();
      setPermissionTemplates(templates);
      setLoading(false);
    } catch (err) {
      setError('خطا در دریافت اطلاعات دسترسی ها');
      setLoading(false);

    }
  };

  // Handle role selection
  const handleRoleSelect = (role: string) => {
    setSelectedRole(role);
    const template = permissionTemplates.find(t => t.role === role);
    setSelectedTemplate(template || null);
  };

  const initializePermissions = async () => {
    try {
      await initializeDefaultPermissions(); // Add this function to your permissionService
      setSuccess('Default permissions initialized successfully');
      fetchPermissions(); // Refresh the permissions list
    } catch (err) {
      setError('Failed to initialize permissions');

    }
  };

  // Toggle section access for selected role
  const handleSectionToggle = (sectionId: string) => {
    if (!selectedRole || !selectedTemplate) return;
    
    const updatedTemplate = { 
      ...selectedTemplate,
      sections: { 
        ...selectedTemplate.sections 
      }
    };
    
    // Initialize section if it doesn't exist
    if (!updatedTemplate.sections[sectionId]) {
      updatedTemplate.sections[sectionId] = { access: false };
    }
    
    // Toggle the access value
    const currentAccess = updatedTemplate.sections[sectionId].access;
    updatedTemplate.sections[sectionId].access = !currentAccess;
    
    // Debug logging

    setSelectedTemplate(updatedTemplate);
    setPermissionTemplates(prev => 
      prev.map(template => 
        template.role === selectedRole ? updatedTemplate : template
      )
    );
  };

  // Save updated permissions to the server
  const savePermissions = async () => {
    if (!selectedRole || !selectedTemplate) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Debug logging

      await updatePermissionTemplate(selectedTemplate._id, selectedTemplate);
      
      setSuccess('تغییرات با موفقیت ذخیره شد');
      setSaving(false);
    } catch (err) {
      setError('خطا در ذخیره تغییرات');
      setSaving(false);

    }
  };

  return (
    <div className="container mx-auto px-4 py-8 direction-rtl">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">مدیریت دسترسی نقش ها</h1>
        
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-700 p-4 rounded-md mb-4">
            {success}
          </div>
        )}
        
        {loading ? (
          <div className="text-center py-8">
            <p>در حال بارگذاری...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="col-span-1">
              <h2 className="text-lg font-semibold mb-4">نقش ها</h2>
              <div className="bg-gray-50 rounded-lg p-2">
                {roles.map(role => (
                  <div 
                    key={role.value}
                    onClick={() => handleRoleSelect(role.value)}
                    className={`p-3 mb-2 rounded-md cursor-pointer ${
                      selectedRole === role.value ? 'bg-blue-100 border border-blue-300' : 'hover:bg-gray-100'
                    }`}
                  >
                    <h3 className="font-medium text-gray-800">{role.name}</h3>
                    <p className="text-sm text-gray-600">{role.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="col-span-3">
              <h2 className="text-lg font-semibold mb-4">دسترسی های بخش ها</h2>
              {!selectedRole ? (
                <div className="bg-yellow-50 text-yellow-700 p-4 rounded-md">
                  لطفا یک نقش را از سمت راست انتخاب کنید.
                </div>
              ) : !selectedTemplate ? (
                <div className="bg-yellow-50 text-yellow-700 p-4 rounded-md">
                  اطلاعات دسترسی برای این نقش یافت نشد. لطفا با پشتیبانی تماس بگیرید.
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-800 mb-4">
                    تعیین دسترسی های نقش: {roles.find(r => r.value === selectedRole)?.name}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sections.map(section => (
                      <div key={section.id} className="flex items-center">
                        <input 
                          type="checkbox" 
                          id={`section-${section.id}`}
                          checked={selectedTemplate.sections[section.id]?.access || false}
                          onChange={() => handleSectionToggle(section.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <label htmlFor={`section-${section.id}`} className="mr-2 text-sm font-medium text-gray-700">
                          {section.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={savePermissions}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-300"
                    >
                      {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessControl;